import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

interface DrawerProps {
  open: boolean;
  title: string;
  /** Frase de apoyo bajo el titulo. */
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  /** Botones de accion fijos en el pie del panel. */
  footer?: ReactNode;
  width?: string;
  /**
   * Donde va el foco al abrir. Por defecto, al primer campo del cuerpo (un
   * formulario quiere escribir ya). `false` = al boton de cerrar: para
   * paneles de lectura como la ayuda, cuyo primer campo esta al final y
   * enfocarlo desplazaba todo el contenido fuera de la vista.
   */
  focusBody?: boolean;
}

/** Panel lateral con foco atrapado y cierre con Escape. */
export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = '32rem',
  focusBody = true,
}: DrawerProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  /*
   * `onClose` casi siempre llega como funcion anonima, asi que cambia de
   * identidad en CADA render. Guardarla en una ref es lo que permite que el
   * efecto de abajo dependa solo de `open`: ver el comentario de alli.
   */
  const cerrarRef = useRef(onClose);
  cerrarRef.current = onClose;

  /*
   * Foco inicial y Escape. Depende SOLO de `open`, y esto es lo importante.
   *
   * Antes dependia tambien de `onClose`: como esa funcion se recrea en cada
   * render, el efecto se repetia con cada tecla y volvia a mover el foco al
   * primer elemento del panel — que en el orden del documento es la "×" de
   * la cabecera. Escribias el nombre de un proveedor, la primera letra te
   * echaba del campo y el resto se perdia (Alex, 2 sep 2026).
   */
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cerrarRef.current();
    };
    document.addEventListener('keydown', onKeyDown);

    const previous = document.activeElement as HTMLElement | null;
    // El primer campo del CUERPO, no el primero del panel: si no, el foco de
    // salida es siempre el boton de cerrar y hay que tabular para escribir.
    const first =
      (focusBody
        ? bodyRef.current?.querySelector<HTMLElement>('input, select, textarea, button, [href]')
        : null) ??
      panelRef.current?.querySelector<HTMLElement>('input, select, textarea, button, [href]');
    first?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div className="drawer-root">
      <div className="drawer__scrim" onClick={onClose} aria-hidden="true" />
      <div
        className="drawer"
        style={{ '--drawer-width': width } as CSSProperties}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
      >
        <header className="drawer__head">
          <div>
            <h2 className="drawer__title">{title}</h2>
            {subtitle && <p className="drawer__subtitle">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="drawer__close"
            onClick={onClose}
            aria-label="Cerrar panel"
          >
            ×
          </button>
        </header>
        <div className="drawer__body" ref={bodyRef}>
          {children}
        </div>
        {footer && <footer className="drawer__foot">{footer}</footer>}
      </div>
    </div>
  );
}
